import { ChevronDown } from "lucide-react";
import { useState } from "react";

import { Card } from "@/components/ui/Card";
import { useRamadanSettings } from "@/hooks/useSettings";
import { cn } from "@/lib/cn";
import { formatIst } from "@/lib/time";

interface Props {
  fajr: Date;
  maghrib: Date;
  /** True when the current IST date is in Ramadan (Hijri month 9). */
  isRamadan: boolean;
}

function offsetMinutes(d: Date, minutes: number): Date {
  return new Date(d.getTime() + minutes * 60_000);
}

function Timings({ sehriEnd, iftar }: { sehriEnd: Date; iftar: Date }) {
  return (
    <dl className="space-y-2.5 text-sm">
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-muted">Sehri ends</dt>
        <dd className="font-serif text-base tabular-nums text-ink">
          {formatIst(sehriEnd, "h:mm a")}
        </dd>
      </div>
      <div className="flex items-baseline justify-between gap-3">
        <dt className="text-muted">Iftar</dt>
        <dd className="font-serif text-base tabular-nums text-ink">
          {formatIst(iftar, "h:mm a")}
        </dd>
      </div>
    </dl>
  );
}

export function RamadanCard({ fajr, maghrib, isRamadan }: Props) {
  const settings = useRamadanSettings();
  const [expanded, setExpanded] = useState(false);

  const sehriEnd = offsetMinutes(fajr, -settings.sehriBufferMinutes);
  const iftar = offsetMinutes(maghrib, settings.iftarBufferMinutes);

  if (isRamadan) {
    return (
      <Card className="border-gold/40 bg-gold/[0.05]">
        <div className="flex items-baseline justify-between gap-3">
          <div>
            <div className="label-eyebrow text-gold">Ramadan</div>
            <h3 className="mt-1 font-serif text-lg text-ink">Sehri &amp; Iftar</h3>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-gold">Today</span>
        </div>
        <div className="mt-4">
          <Timings sehriEnd={sehriEnd} iftar={iftar} />
        </div>
      </Card>
    );
  }

  return (
    <div>
      <button
        type="button"
        onClick={() => setExpanded((v) => !v)}
        aria-expanded={expanded}
        aria-controls="ramadan-timings"
        className="flex w-full items-center justify-between rounded-card border border-line bg-cream/60 px-4 py-3 text-left transition-colors hover:bg-cream focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-masjidGreen"
      >
        <div>
          <div className="label-eyebrow">Sehri &amp; Iftar</div>
          <div className="mt-0.5 font-serif text-base text-ink">
            {expanded ? "Hide" : "View"} today&rsquo;s Sehri &amp; Iftar times
          </div>
        </div>
        <ChevronDown
          size={20}
          aria-hidden
          className={cn(
            "shrink-0 text-masjidGreen transition-transform",
            expanded && "rotate-180",
          )}
        />
      </button>
      {expanded ? (
        <div id="ramadan-timings" className="mt-3">
          <Card>
            <Timings sehriEnd={sehriEnd} iftar={iftar} />
          </Card>
        </div>
      ) : null}
    </div>
  );
}
